// PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import { 
  Shield, Lock, Users, Mail,
  FileText, CheckCircle, Eye, Star, Globe, Key, Database,
  ShieldCheck, UserCheck, ArrowRight
} from 'lucide-react';

const PrivacyPolicy = () => {
  const companyName = "Laminance";
  const contactEmail = "contactus@laminance.com";
  const websiteUrl = "https://www.laminance.com";

  // SEO Meta Tags
  useEffect(() => {
    document.title = `Privacy Policy | ${companyName} - Your Data Protection Partner`;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      const desc = document.createElement('meta');
      desc.name = 'description';
      desc.content = `Read ${companyName}'s comprehensive Privacy Policy. Learn how we collect, use, and protect your personal information with transparency and care.`;
      document.head.appendChild(desc);
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${websiteUrl}/privacy-policy`;
    document.head.appendChild(canonical);

    return () => {
      document.head.removeChild(canonical);
    };
  }, []);

  const policySections = [
    { id: 'introduction', title: 'Introduction', icon: Shield },
    { id: 'data-collection', title: 'Data We Collect', icon: Database },
    { id: 'data-usage', title: 'How We Use Data', icon: Globe },
    { id: 'data-protection', title: 'Data Protection', icon: Lock },
    { id: 'your-rights', title: 'Your Rights', icon: UserCheck },
    { id: 'contact', title: 'Contact Us', icon: Mail }
  ];

  const userRights = [
    { title: "Right to Access", description: "Request access to your personal data", icon: Eye },
    { title: "Right to Correction", description: "Correct inaccurate personal data", icon: CheckCircle },
    { title: "Right to Deletion", description: "Request deletion of your personal data", icon: ShieldCheck },
    { title: "Right to Object", description: "Object to processing of your data", icon: Users },
  ];

  const dataUses = [
    "To provide and maintain our services",
    "To notify you about changes to our services",
    "To allow you to participate in interactive features",
    "To provide customer care and support",
    "To provide analysis or valuable information",
    "To monitor the usage of our services",
    "To detect, prevent and address technical issues"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-orange-50/20 to-white ">
      

      {/* Hero Section - Fixed */}
      <div className="relative bg-gradient-to-r from-orange-500 to-amber-500 text-white py-12 md:py-16">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
        </div>
        <div className="container relative mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
           
            <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight mt-10">
              Your Privacy Matters
            </h1>
            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              At {companyName}, we believe in transparency and trust. This Privacy Policy outlines 
              how we handle your personal information with the outmost care and respect.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                <span>GDPR Compliant</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
                <ShieldCheck className="w-4 h-4 mr-2" />
                <span>Data Protection</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
                <UserCheck className="w-4 h-4 mr-2" />
                <span>Your Control</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:w-1/4">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8 border border-orange-100">
              <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-orange-600" />
                Policy Sections
              </h3>
              <nav className="space-y-2">
                {policySections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center p-3 text-gray-600 hover:text-orange-700 hover:bg-orange-50 rounded-xl transition-all duration-300 group"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg flex items-center justify-center mr-3 group-hover:from-orange-100 group-hover:to-orange-200 transition-all">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{section.title}</div>
                        <div className="text-xs text-gray-400">Section {index + 1}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  );
                })}
              </nav>
              
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <h4 className="font-bold text-gray-700 mb-2 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-blue-600" />
                    Quick Summary
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                      <span>No data selling or sharing</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                      <span>Industry-standard encryption</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                      <span>Easy data access & control</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="lg:w-3/4">
            {/* Introduction */}
            <section id="introduction" className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mr-4">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">1. Introduction</h2>
                    <p className="text-gray-500">Our commitment to your privacy</p>
                  </div>
                </div>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Welcome to {companyName}'s Privacy Policy. Your privacy is critically important to us, 
                    and we are committed to protecting your personal information. This policy explains what 
                    information we collect, how we use it, and what rights you have in relation to it.
                  </p>
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 p-5 rounded-r-lg my-6">
                    <p className="text-orange-800 font-medium">
                      <strong>Our Promise:</strong> We will never sell your personal data to third parties.
                    </p>
                  </div>
                  <p className="text-gray-700">
                    By using our website {websiteUrl}, you agree to the collection and use of information 
                    in accordance with this policy. If you have any questions or concerns about our use of 
                    your personal information, please contact us using the details provided below.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Collection */}
            <section id="data-collection" className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                    <Database className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">2. Data We Collect</h2>
                    <p className="text-gray-500">Information you provide to us</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-6">
                    <h3 className="font-bold text-gray-800 text-xl mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-blue-600" />
                      Personal Information
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Full name & contact details
                      </li>
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Email address & phone number
                      </li>
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Physical address & location
                      </li>
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Professional information (if applicable)
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-100 rounded-xl p-6">
                    <h3 className="font-bold text-gray-800 text-xl mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-orange-600" />
                      Communications
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                        Email correspondence
                      </li>
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                        Phone call records
                      </li>
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                        Contact form submissions
                      </li>
                      <li className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                        Customer feedback & inquiries
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* How We Use Data */}
            <section id="data-usage" className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">3. How We Use Your Data</h2>
                    <p className="text-gray-500">Purpose of data collection</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-8">
                  <h3 className="font-bold text-gray-800 text-xl mb-6 text-center">
                    Your data helps us provide better service
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {dataUses.map((use, index) => (
                      <div key={index} className="flex items-start">
                        <div className="bg-white w-8 h-8 rounded-lg flex items-center justify-center mr-3 shadow-sm mt-1">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">{use}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Data Protection */}
            <section id="data-protection" className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">4. Data Protection</h2>
                    <p className="text-gray-500">How we keep your information safe</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-b from-purple-50 to-white border border-purple-100 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-800 mb-3">Encryption</h3>
                    <p className="text-gray-600 text-sm">
                      All sensitive data is encrypted using industry-standard protocols
                    </p>
                  </div>
                  <div className="bg-gradient-to-b from-blue-50 to-white border border-blue-100 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <ShieldCheck className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-800 mb-3">Access Control</h3>
                    <p className="text-gray-600 text-sm">
                      Strict access controls and authentication for authorized personnel only
                    </p>
                  </div>
                  <div className="bg-gradient-to-b from-green-50 to-white border border-green-100 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Database className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-800 mb-3">Secure Storage</h3>
                    <p className="text-gray-600 text-sm">
                      Data stored on secure servers with regular security updates
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section id="your-rights" className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
                    <UserCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">5. Your Rights</h2>
                    <p className="text-gray-500">Control over your personal information</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {userRights.map((right, index) => {
                    const Icon = right.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-gradient-to-b from-white to-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-orange-300 transition-all duration-300"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-orange-600" />
                        </div>
                        <h3 className="font-bold text-gray-800 mb-2">{right.title}</h3>
                        <p className="text-gray-600 text-sm">{right.description}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
                  <h3 className="font-bold text-orange-800 text-lg mb-3">How to Exercise Your Rights</h3>
                  <p className="text-orange-700 mb-4">
                    To exercise any of these rights, please contact our Data Protection Officer at:
                  </p>
                  <a 
                    href={`mailto:${contactEmail}`}
                    className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3 rounded-lg transition-colors"
                  >
                    {contactEmail}
                  </a>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;